import { supabase } from '../../lib/supabaseClient';
import { buildRevisionStoragePath, buildStoragePath, looksLikePdf, MAX_FILE_SIZE, preferSignedStoragePath } from './fileUtils';
import type { Database, Json } from '../../types/database.types';

export type Folder = Database['public']['Tables']['dossier_folders']['Row'];
export type DossierFile = Database['public']['Tables']['dossier_files']['Row'] & {
  signature_circuit?: { id: string; status: string; initiator_id: string; kind?: string } | null;
};

const DOSSIER_FILE_SELECT =
  '*, signature_circuit:signature_circuits!dossier_files_signature_circuit_id_fkey(id, status, initiator_id, kind)';

const BUCKET = 'dossier-files';

export async function logActivity(input: {
  action: string;
  dossierId: string;
  subjectType: string;
  subjectId: string;
  details: Record<string, unknown>;
}) {
  // Best-effort : on n'échoue pas la mutation principale si le log échoue
  try {
    const actorId = (await supabase.auth.getUser()).data.user?.id ?? null;
    await supabase.from('dossier_activity_log').insert({
      dossier_id:   input.dossierId,
      actor_id:     actorId,
      action:       input.action,
      subject_type: input.subjectType,
      subject_id:   input.subjectId,
      details:      input.details as Json,
    });
  } catch {
    /* swallow */
  }
}

export async function listFolders(dossierId: string, parentId: string | null): Promise<Folder[]> {
  const q = supabase
    .from('dossier_folders')
    .select('*')
    .eq('dossier_id', dossierId)
    .order('name', { ascending: true });
  const { data, error } = parentId === null
    ? await q.is('parent_id', null)
    : await q.eq('parent_id', parentId);
  if (error) throw error;
  return data ?? [];
}

export async function listFiles(dossierId: string, folderId: string | null): Promise<DossierFile[]> {
  const q = supabase
    .from('dossier_files')
    .select(DOSSIER_FILE_SELECT)
    .eq('dossier_id', dossierId)
    .order('created_at', { ascending: false });
  const { data, error } = folderId === null
    ? await q.is('folder_id', null)
    : await q.eq('folder_id', folderId);
  if (error) throw error;
  return (data ?? []) as DossierFile[];
}

export async function createFolder(input: {
  dossierId: string;
  parentId: string | null;
  name: string;
  createdBy: string;
}): Promise<Folder> {
  const { data, error } = await supabase
    .from('dossier_folders')
    .insert({
      dossier_id: input.dossierId,
      parent_id: input.parentId,
      name: input.name.trim(),
      created_by: input.createdBy,
    })
    .select()
    .single();
  if (error) throw error;
  await logActivity({
    action: 'folder.create',
    dossierId: data.dossier_id,
    subjectType: 'dossier_folder',
    subjectId: data.id,
    details: { name: data.name, parent_id: data.parent_id },
  });
  return data;
}

export async function renameFolder(folderId: string, name: string): Promise<Folder> {
  const { data, error } = await supabase
    .from('dossier_folders')
    .update({ name: name.trim() })
    .eq('id', folderId)
    .select()
    .single();
  if (error) throw error;
  await logActivity({
    action: 'folder.rename',
    dossierId: data.dossier_id,
    subjectType: 'dossier_folder',
    subjectId: data.id,
    details: { new_name: data.name },
  });
  return data;
}

export async function deleteFolder(folderId: string): Promise<void> {
  const { data: row } = await supabase
    .from('dossier_folders').select('dossier_id, name').eq('id', folderId).maybeSingle();
  const { error } = await supabase.from('dossier_folders').delete().eq('id', folderId);
  if (error) throw error;
  if (row) {
    await logActivity({
      action: 'folder.delete',
      dossierId: row.dossier_id,
      subjectType: 'dossier_folder',
      subjectId: folderId,
      details: { name: row.name },
    });
  }
}

export async function uploadFile(input: {
  dossierId: string;
  folderId: string | null;
  uploaderId: string;
  file: File;
  onProgress?: (pct: number) => void;
  source?: string;
  sourceRef?: string;
}): Promise<DossierFile> {
  if (input.file.size > MAX_FILE_SIZE) {
    throw new Error('Fichier trop volumineux (max 50 Mo).');
  }
  const fileId = crypto.randomUUID();
  const storagePath = buildStoragePath(input.dossierId, fileId, input.file.name);

  const mime = looksLikePdf(input.file.type, input.file.name)
    ? 'application/pdf'
    : (input.file.type || 'application/octet-stream');

  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, input.file, {
      cacheControl: '3600',
      upsert: false,
      contentType: mime,
    });
  if (upErr) throw upErr;
  input.onProgress?.(100);

  const { data, error } = await supabase
    .from('dossier_files')
    .insert({
      id: fileId,
      dossier_id: input.dossierId,
      folder_id: input.folderId,
      storage_path: storagePath,
      filename: input.file.name,
      mime,
      size: input.file.size,
      uploader_id: input.uploaderId,
      ...(input.source ? { source: input.source } : {}),
      ...(input.sourceRef ? { source_ref: input.sourceRef } : {}),
    })
    .select()
    .single();

  if (error) {
    await supabase.storage.from(BUCKET).remove([storagePath]).catch(() => {});
    throw error;
  }
  await logActivity({
    action: 'file.upload',
    dossierId: input.dossierId,
    subjectType: 'dossier_file',
    subjectId: data.id,
    details: { filename: data.filename, size: data.size, folder_id: data.folder_id },
  });
  return data;
}

export async function replaceFileContents(fileId: string, file: File): Promise<void> {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('Fichier trop volumineux (max 50 Mo).');
  }
  const { data: row, error: getErr } = await supabase
    .from('dossier_files')
    .select('storage_path, signed_storage_path, dossier_id, filename')
    .eq('id', fileId)
    .single();
  if (getErr) throw getErr;
  const newPath = buildRevisionStoragePath(row.dossier_id, fileId, file.name || row.filename);
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(newPath, file, {
      cacheControl: '0',
      upsert: false,
      contentType: file.type || 'application/pdf',
    });
  if (upErr) throw upErr;
  const { error } = await supabase
    .from('dossier_files')
    .update({
      storage_path: newPath,
      filename: file.name || row.filename,
      mime: file.type || 'application/pdf',
      size: file.size,
    })
    .eq('id', fileId);
  if (error) {
    await supabase.storage.from(BUCKET).remove([newPath]).catch(() => {});
    throw error;
  }
  const oldPath = row.storage_path;
  if (oldPath && oldPath !== newPath && oldPath !== row.signed_storage_path) {
    await supabase.storage.from(BUCKET).remove([oldPath]).catch(() => {});
  }
  await logActivity({
    action: 'file.replace',
    dossierId: row.dossier_id,
    subjectType: 'dossier_file',
    subjectId: fileId,
    details: { filename: file.name || row.filename, size: file.size },
  });
}

export async function deleteFile(fileId: string): Promise<void> {
  const { data: row, error: getErr } = await supabase
    .from('dossier_files')
    .select('storage_path, dossier_id, filename')
    .eq('id', fileId)
    .single();
  if (getErr) throw getErr;
  await supabase.storage.from(BUCKET).remove([row.storage_path]).catch(() => {});
  const { error } = await supabase.from('dossier_files').delete().eq('id', fileId);
  if (error) throw error;
  await logActivity({
    action: 'file.delete',
    dossierId: row.dossier_id,
    subjectType: 'dossier_file',
    subjectId: fileId,
    details: { filename: row.filename },
  });
}

export async function getDownloadUrl(
  fileId: string,
  opts?: { preferSigned?: boolean },
): Promise<string> {
  const { data: row, error: getErr } = await supabase
    .from('dossier_files')
    .select('storage_path, signed_storage_path')
    .eq('id', fileId)
    .single();
  if (getErr) throw getErr;
  const path = opts?.preferSigned === false
    ? row.storage_path
    : preferSignedStoragePath(row.storage_path, row.signed_storage_path);
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, 60 * 60); // 1h
  if (error) throw error;
  return data.signedUrl;
}

/** Déplace un fichier vers un autre folder (ou la racine si folderId null). */
export async function moveFile(fileId: string, toFolderId: string | null): Promise<DossierFile> {
  const { data: before } = await supabase
    .from('dossier_files')
    .select('folder_id, filename, dossier_id')
    .eq('id', fileId)
    .single();

  const { data, error } = await supabase
    .from('dossier_files')
    .update({ folder_id: toFolderId })
    .eq('id', fileId)
    .select()
    .single();
  if (error) throw error;

  await logActivity({
    action: 'file.move',
    dossierId: data.dossier_id,
    subjectType: 'dossier_file',
    subjectId: data.id,
    details: { filename: data.filename, from_folder_id: before?.folder_id ?? null, to_folder_id: toFolderId },
  });
  return data;
}

/** Change la visibilité d'un fichier (internal / shared_client). */
export async function setFileVisibility(
  fileId: string,
  visibility: 'internal' | 'shared_client',
): Promise<DossierFile> {
  const { data, error } = await supabase
    .from('dossier_files')
    .update({ visibility })
    .eq('id', fileId)
    .select()
    .single();
  if (error) throw error;

  await logActivity({
    action: 'file.visibility_change',
    dossierId: data.dossier_id,
    subjectType: 'dossier_file',
    subjectId: data.id,
    details: { filename: data.filename, visibility },
  });
  return data;
}

/** Tous les fichiers du dossier, tous folders confondus (pour la recherche). */
export async function listAllFiles(dossierId: string): Promise<DossierFile[]> {
  const { data, error } = await supabase
    .from('dossier_files')
    .select(DOSSIER_FILE_SELECT)
    .eq('dossier_id', dossierId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as DossierFile[];
}

/**
 * Vue globale : tous les fichiers visibles par l'utilisateur (RLS scope les
 * dossiers accessibles), avec la référence + l'intitulé du dossier rattaché.
 * Pour la page « Fichiers » transverse.
 */
export interface DossierFileWithDossier extends DossierFile {
  dossier: { id: string; number: string; title: string } | null;
}

export async function listAllCabinetFiles(): Promise<DossierFileWithDossier[]> {
  const { data, error } = await supabase
    .from('dossier_files')
    .select('*, dossier:dossiers(id, number, title)')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as DossierFileWithDossier[];
}

/** Fichiers partagés avec le client (visibilité « shared_client ») dans une liste de dossiers. */
export async function listSharedFilesForDossiers(dossierIds: string[]): Promise<DossierFileWithDossier[]> {
  if (dossierIds.length === 0) return [];
  const { data, error } = await supabase
    .from('dossier_files')
    .select('*, dossier:dossiers(id, number, title)')
    .in('dossier_id', dossierIds)
    .eq('visibility', 'shared_client')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as DossierFileWithDossier[];
}

/** Tous les folders du dossier, tous niveaux confondus (pour résoudre les chemins). */
export async function listAllFolders(dossierId: string): Promise<Folder[]> {
  const { data, error } = await supabase
    .from('dossier_folders')
    .select('*')
    .eq('dossier_id', dossierId)
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export type ApplyFolderTemplateResult = {
  applied: boolean;
  skipped: boolean;
  template: string;
  created: number;
};

/** Pose un gabarit Conseil/Contentieux à la racine. No-op si des sous-dossiers existent. */
export async function applyFolderTemplate(
  dossierId: string,
  template: string,
): Promise<ApplyFolderTemplateResult> {
  const { data, error } = await supabase.rpc('apply_dossier_folder_template', {
    p_dossier_id: dossierId,
    p_template: template,
  });
  if (error) throw error;
  return data as ApplyFolderTemplateResult;
}
